import { useCallback, useMemo, useRef } from 'react';

import styled from '@emotion/styled';
import { Button, Tooltip, Typography, useMediaQuery } from '@kc-console/common-ui';
import { useServiceAppState } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { ShortcutType, useShortcuts } from '@kc-console/shared-ui';
import { IcSearchRegular } from '@kep/kcds-icons';

import { MediaQuery } from '../../../../constants/media';
import SearchPopover from './SearchPopover';
import { useSearchPopover } from './SearchPopoverProvider';

const SearchButton = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const { popoverProps } = useSearchPopover();
  const {
    state: { region },
  } = useServiceAppState();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const hasRegion = useMemo(() => !!region, [region]);
  const tooltipMessage = useMemo(
    () => (!hasRegion ? t('layout:search.button.select_project') : ''),
    [hasRegion, t],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!hasRegion) {
        return;
      }
      popoverProps.onClick(e);
    },
    [hasRegion, popoverProps],
  );

  useShortcuts({
    type: ShortcutType.OpenSearchBar,
    behavior: () => {
      if (!hasRegion) {
        return;
      }
      if (buttonRef.current) {
        if (!popoverProps.open) {
          popoverProps.onOpen(buttonRef.current);
          return;
        }
        popoverProps.onClose();
      }
    },
  });

  return (
    <>
      <Tooltip message={tooltipMessage}>
        <StyledButton ref={buttonRef} variant={'primary'} onClick={handleClick}>
          <ButtonTextContainer>
            <SearchIcon />
            <Typography variant={'label/medium/md'}>
              {isMobile
                ? t('layout:search.button.label')
                : `${t('layout:search.button.placeholder')} (⌘K)`}
            </Typography>
          </ButtonTextContainer>
        </StyledButton>
      </Tooltip>
      <SearchPopover />
    </>
  );
};

const StyledButton = styled(Button)`
  width: 100%;
  display: flex;
  color: #878e9b;
  .MuiButton-icon {
    font-size: 16px;
  }
  background-color: #13161999; /* theme.color['surface/global/transparent/weaker/Default']}; */

  &:hover {
    background-color: #131619cc; /* theme.color['surface/global/transparent/weaker/hover */
  }

  &:active {
    background-color: #131619f2; /* theme.color['surface/global/transparent/weaker/press']};*/
  }

  ${({ theme }) => theme.mixins.focusRing('inside', { inverse: true })};

  ${MediaQuery.TIL_SMALL} {
    min-width: 179px;
  }

  ${MediaQuery.MEDIUM} {
    min-width: 419px;
    max-width: calc(923px - 48px);
  }

  ${MediaQuery.LARGE} {
    min-width: calc(923px - 48px);
  }
`;

const ButtonTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SearchIcon = styled(IcSearchRegular)`
  width: 16px;
  height: 16px;
  margin: 2px;
`;

export default SearchButton;
